import '~/assets/styles/index.scss';

import React from 'react';
import { GeoLocationProvider } from '~/app/context';
import { runCtmWhenReady } from '~/shared/utils/ctm';

// ─── URL lowercase redirect ───────────────────────────────────────────────────
export const onInitialClientRender = () => {
  if (typeof window !== 'undefined' && window.location.pathname !== window.location.pathname.toLowerCase()) {
    window.location.href = window.location.origin + window.location.pathname.toLowerCase();
  }
};

// ─── Attribution config ───────────────────────────────────────────────────────
const ATTR_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'gclid', 'fbclid', 'msclkid',
  'cq_cmp', 'cq_con', 'cq_med', 'cq_net', 'cq_plt', 'cq_src', 'cq_term',
  'city',
];
const TOUCH_STORAGE = { first: 'traffic_first_touch', last: 'traffic_last_touch' };
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;
const GTM_CODE = process.env.GTM_CODE;

let attributionInitialized = false;
let gtmInitialized = false;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getCookie = (name) => {
  if (typeof document === 'undefined') return '';
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  return parts.length === 2 ? parts.pop().split(';').shift() : '';
};

const setCookie = (name, value, maxAge = COOKIE_MAX_AGE_SECONDS) => {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; samesite=lax`;
};

const safeJsonParse = (value, fallback) => {
  try { return JSON.parse(value); } catch (_) { return fallback; }
};

const readParams = () => {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const data = {};
  ATTR_KEYS.forEach((key) => { const v = params.get(key); if (v) data[key] = v; });
  return data;
};

const buildTouchPayload = (params) => {
  if (typeof window === 'undefined') return {};
  return {
    ...params,
    page_url: window.location.href,
    page_path: window.location.pathname,
    referrer: document.referrer || '',
    timestamp: new Date().toISOString(),
  };
};

// ─── Attribution persistence ──────────────────────────────────────────────────
const persistAttribution = () => {
  if (typeof window === 'undefined' || attributionInitialized) return;
  attributionInitialized = true;

  try {
    const params = readParams();
    const touchPayload = buildTouchPayload(params);
    const firstTouchStored = window.localStorage.getItem(TOUCH_STORAGE.first);
    const firstTouch = firstTouchStored ? safeJsonParse(firstTouchStored, touchPayload) : touchPayload;
    const lastTouch = touchPayload;

    if (!firstTouchStored) {
      window.localStorage.setItem(TOUCH_STORAGE.first, JSON.stringify(firstTouch));
    }
    window.localStorage.setItem(TOUCH_STORAGE.last, JSON.stringify(lastTouch));

    Object.entries(firstTouch).forEach(([key, value]) => setCookie(`ctm_attr_first_${key}`, String(value)));
    Object.entries(lastTouch).forEach(([key, value]) => setCookie(`ctm_attr_last_${key}`, String(value)));

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'traffic_context_ready',
      page_url: window.location.href,
      page_path: window.location.pathname,
      referrer: document.referrer || '',
      source_data: { first_touch: firstTouch, last_touch: lastTouch },
      first_touch: firstTouch,
      last_touch: lastTouch,
    });
  } catch (_) {}
};

// ─── GTM ──────────────────────────────────────────────────────────────────────
const loadGtm = () => {
  if (typeof window === 'undefined' || !GTM_CODE || gtmInitialized) return;
  gtmInitialized = true;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CODE}`;
  document.head.appendChild(script);
};

const installDeferredGtmLoader = () => {
  if (typeof window === 'undefined' || !GTM_CODE) return;

  let armed = true;
  const passiveOptions = { passive: true };
  const trigger = () => {
    if (!armed) return;
    armed = false;
    loadGtm();
    window.removeEventListener('scroll', trigger, passiveOptions);
    window.removeEventListener('pointerdown', trigger, passiveOptions);
    window.removeEventListener('keydown', trigger);
    window.removeEventListener('touchstart', trigger, passiveOptions);
  };

  window.addEventListener('scroll', trigger, passiveOptions);
  window.addEventListener('pointerdown', trigger, passiveOptions);
  window.addEventListener('keydown', trigger);
  window.addEventListener('touchstart', trigger, passiveOptions);

  const isPaidTraffic = ['gclid', 'fbclid', 'msclkid', 'utm_source', 'utm_campaign'].some((key) =>
    new URLSearchParams(window.location.search).has(key)
  );

  // Paid: still auto-load after idle. Organic: interaction only — keeps main thread free for Lighthouse / cold landings.
  if (isPaidTraffic) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => trigger(), { timeout: 4000 });
    }
    setTimeout(trigger, 5000);
  }
};

// ─── FB Pixel server-side tracking ───────────────────────────────────────────
const generateEventId = () => `_lp_${Math.random().toString(36).slice(2, 11)}`;

const sendTrackEvent = (event_name) => {
  if (typeof window === 'undefined') return;
  fetch('/api/trackEvent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event_name,
      event_id: generateEventId(),
      event_source_url: window.location.href,
      client_user_agent: window.navigator.userAgent,
      fbc: getCookie('_fbc'),
      fbp: getCookie('_fbp'),
    }),
  }).catch(() => null);
};

// ─── Phone click tracking ─────────────────────────────────────────────────────
const installPhoneClickTracking = () => {
  if (typeof document === 'undefined') return;
  document.addEventListener('click', (event) => {
    const anchor = event.target?.closest?.('a[href^="tel:"]');
    if (!anchor) return;
    sendTrackEvent('Lead');
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'phone_click', phone_href: anchor.getAttribute('href') || '' });
  });
};

// ─── Init ─────────────────────────────────────────────────────────────────────
const runDeferredInit = () => {
  persistAttribution();
  installDeferredGtmLoader();
  installPhoneClickTracking();
  runCtmWhenReady();
};

const scheduleDeferredInit = () => {
  if (typeof window === 'undefined') return;

  const run = () => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(runDeferredInit, { timeout: 3000 });
      return;
    }
    setTimeout(runDeferredInit, 1);
  };

  if (document.readyState === 'complete') {
    run();
    return;
  }

  window.addEventListener('load', run, { once: true });
};

scheduleDeferredInit();

const runWhenIdle = (fn, timeout = 5000) => {
  if (typeof window === 'undefined') return;
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(fn, { timeout });
    return;
  }
  setTimeout(fn, 2000);
};

export const onRouteUpdate = () => {
  persistAttribution();
  runWhenIdle(() => {
    sendTrackEvent('PageView');
    runCtmWhenReady();
  });
  // SPA navigation signal (replaces gatsby-plugin-google-tagmanager route change event)
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'gatsby-route-change' });
};

export const wrapRootElement = ({ element }) => {
  return <GeoLocationProvider>{element}</GeoLocationProvider>;
};
